import { useMemo, useState } from "react";

import { AgeingBar } from "../components/charts.js";
import { Card, Empty, Money, Row, Rows, SectionTitle, Segmented } from "../components/ui.js";
import { AGEING_BUCKETS, deriveOutstanding } from "../derive/outstanding.js";
import type { PartyOutstanding } from "../derive/outstanding.js";
import { formatDate, formatMoney } from "../derive/reports.js";
import { ShareBar } from "../components/ShareBar.js";
import { navigate } from "../routes.js";
import { outstandingMessage, reminderMessage } from "../share.js";
import { useApp } from "../state.js";

export function Outstanding() {
  const { snapshot, groups, asOf } = useApp();
  const [side, setSide] = useState<"receivable" | "payable">("receivable");
  const [open, setOpen] = useState<string | null>(null);

  const report = useMemo(
    () => (snapshot && groups ? deriveOutstanding(snapshot, asOf, groups) : null),
    [snapshot, groups, asOf],
  );

  if (!report) return <Empty>Nothing loaded.</Empty>;

  const parties = side === "receivable" ? report.receivables : report.payables;
  const buckets = side === "receivable" ? report.receivableBuckets : report.payableBuckets;
  const total = side === "receivable" ? report.totalReceivable : report.totalPayable;
  const unallocated = report.unallocated.filter((r) => r.kind === side);

  const ageing = AGEING_BUCKETS.map((b) => ({
    label: b === "90+" ? "90+ days" : `${b} days`,
    value: buckets[b],
  }));

  return (
    <div className="space-y-5">
      <Segmented
        value={side}
        onChange={setSide}
        options={[
          { value: "receivable", label: "They owe you" },
          { value: "payable", label: "You owe" },
        ]}
      />

      <Card>
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <span className="text-[30px] font-semibold leading-none tracking-tight text-ink">
            {formatMoney(total)}
          </span>
          <span className="text-xs text-ink-3">
            {parties.length} {parties.length === 1 ? "party" : "parties"}
          </span>
        </div>
        <AgeingBar data={ageing} total={total} />
      </Card>

      <ShareBar
        actions={[
          {
            label: side === "receivable" ? "Share receivables" : "Share payables",
            build: () => ({
              title: side === "receivable" ? "Receivables" : "Payables",
              body: outstandingMessage({
                company: snapshot?.company.name ?? "",
                kind: side,
                parties: parties.map((p) => ({ ledger: p.ledger, total: p.total })),
                total,
                asOf,
              }),
            }),
          },
        ]}
        csv={[
          {
            label: "Export CSV",
            filename: side === "receivable" ? "receivables" : "payables",
            build: () => [
              ["Party", "Bill", "Bill date", "Due date", "Days overdue", "Bucket", "Amount"],
              ...parties.flatMap((party) =>
                party.bills.map((bill) => [
                  party.ledger,
                  bill.billName,
                  bill.billDate,
                  bill.dueDate,
                  bill.daysOverdue,
                  bill.bucket,
                  bill.amount.toFixed(2),
                ]),
              ),
              // Bill-less balances are part of what is owed; leaving them out of
              // the export would make the file disagree with the screen total.
              ...unallocated.map((row) => [
                row.ledger,
                "(no bill reference)",
                "",
                "",
                "",
                "",
                row.amount.toFixed(2),
              ]),
            ],
          },
        ]}
      />

      {parties.length === 0 ? (
        <Empty>Nothing outstanding.</Empty>
      ) : (
        <div>
          <SectionTitle>By party · oldest debt first</SectionTitle>
          <Card flush>
            <Rows>
              {parties.map((party) => (
                <PartyRows
                  key={`${party.ledger}-${party.kind}`}
                  party={party}
                  expanded={open === party.ledger}
                  onToggle={() => setOpen(open === party.ledger ? null : party.ledger)}
                  company={snapshot?.company.name ?? ""}
                  asOf={asOf}
                />
              ))}
            </Rows>
          </Card>
        </div>
      )}

      {unallocated.length > 0 && (
        <div>
          <SectionTitle>Not covered by any bill</SectionTitle>
          <Card flush>
            {/* An opening balance from before the export window, or an entry
                booked without a bill reference. Dropping these would understate
                what is owed, so they are shown apart rather than merged in. */}
            <Rows>
              {unallocated.map((row) => (
                <Row
                  key={row.ledger}
                  avatar
                  title={row.ledger}
                  sub="no bill reference"
                  right={<Money amount={row.amount} />}
                  onClick={() => navigate({ id: "ledger", param: row.ledger })}
                />
              ))}
            </Rows>
          </Card>
        </div>
      )}
    </div>
  );
}

/** Which ageing step a bucket sits on, so a row's dot matches the bar above. */
const BUCKET_TOKEN: Record<string, string> = {
  "0-30": "var(--age-1)",
  "31-60": "var(--age-2)",
  "61-90": "var(--age-3)",
  "90+": "var(--age-4)",
};

function PartyRows({
  party,
  expanded,
  onToggle,
  company,
  asOf,
}: {
  party: PartyOutstanding;
  expanded: boolean;
  onToggle: () => void;
  company: string;
  asOf: string;
}) {
  const worst = party.bills[0];
  const dot = worst ? BUCKET_TOKEN[worst.bucket] : "var(--age-1)";

  return (
    <>
      <li>
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-surface-2"
        >
          <span
            className="h-9 w-1.5 shrink-0 rounded-full"
            style={{ background: dot }}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] leading-snug text-ink">{party.ledger}</span>
            <span className="mt-0.5 block truncate text-xs text-ink-3">
              {party.bills.length} {party.bills.length === 1 ? "bill" : "bills"}
              {worst && worst.daysOverdue > 0 && ` · oldest ${worst.daysOverdue} days overdue`}
            </span>
          </span>
          <span className="shrink-0 text-[15px] font-medium text-ink">
            <Money amount={party.total} />
          </span>
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 shrink-0 text-ink-3 transition-transform"
            style={{ transform: expanded ? "rotate(90deg)" : "none" }}
            aria-hidden
          >
            <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </li>

      {expanded && (
        <li className="bg-surface-2 px-4 py-3">
          <ul className="space-y-2">
            {party.bills.map((bill) => (
              <li key={bill.billName} className="flex items-baseline gap-2.5 text-xs">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: BUCKET_TOKEN[bill.bucket] }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-ink-2">{bill.billName}</span>
                <span className="shrink-0 text-ink-3">
                  due {formatDate(bill.dueDate)}
                  {bill.daysOverdue > 0 && ` · +${bill.daysOverdue}d`}
                </span>
                <span className="tabular shrink-0 font-medium text-ink">
                  {formatMoney(bill.amount)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-4">
            <button
              type="button"
              className="text-xs font-medium text-accent"
              onClick={() => navigate({ id: "ledger", param: party.ledger })}
            >
              Open ledger →
            </button>

            {/* A reminder is only ever offered for money owed TO us. Sending a
                supplier a demand for our own payable would be embarrassing. */}
            {party.kind === "receivable" && (
              <ShareBar
                actions={[
                  {
                    label: "Send reminder",
                    build: () => ({
                      title: `${party.ledger} — payment reminder`,
                      body: reminderMessage({
                        company,
                        party: party.ledger,
                        total: party.total,
                        asOf,
                        bills: party.bills.map((b) => ({
                          billName: b.billName,
                          dueDate: b.dueDate,
                          amount: b.amount,
                          daysOverdue: b.daysOverdue,
                        })),
                      }),
                    }),
                  },
                ]}
              />
            )}
          </div>
        </li>
      )}
    </>
  );
}
